import { cva, type VariantProps } from 'class-variance-authority'

/**
 * Category Cards CVA Configuration
 *
 * Styles for homepage category card grid with image + title layout
 */

/**
 * Category cards grid container
 * Matches legacy tools.store.ro layout: 2 mobile / 3 tablet / 5 desktop, tight gap.
 */
export const categoryCardsGridVariants = cva(
  'grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-2 md:gap-2.5'
)

export type CategoryCardsGridVariants = VariantProps<typeof categoryCardsGridVariants>

/**
 * Individual category card - full image, square aspect, white border that
 * darkens on hover (border reserves space so layout doesn't shift).
 */
export const categoryCardItemVariants = cva([
  'group relative block aspect-square overflow-hidden bg-white no-underline',
  'border-2 border-white hover:border-text-color',
  'transition-colors duration-300',
  'text-text-color visited:text-text-color hover:text-text-color active:text-text-color focus:text-text-color',
  'mb-2',
])

export type CategoryCardItemVariants = VariantProps<typeof categoryCardItemVariants>

/**
 * Category card image - fills entire card, subtle hover zoom.
 * object-contain shows full image; letterbox space filled by parent bg-white.
 */
export const categoryCardImageVariants = cva([
  'w-full h-full object-contain',
  'group-hover:scale-105 transition-transform duration-500',
])

export type CategoryCardImageVariants = VariantProps<typeof categoryCardImageVariants>

/**
 * Category card title - orange tab anchored bottom-left of the card,
 * rounded only on the right edge (legacy spec: border-radius 0 200px 200px 0).
 * Hidden on mouse devices until the card is hovered or keyboard-focused;
 * always visible on touch devices (no real hover there).
 */
export const categoryCardTitleVariants = cva([
  'absolute left-0 bottom-[12%]',
  '[@media(hover:hover)]:opacity-0 group-hover:opacity-100 group-focus-visible:opacity-100',
  'transition-opacity duration-200',
  'bg-main-color text-text-color',
  'font-bold font-main-font uppercase tracking-wide',
  'text-sm md:text-base xl:text-lg',
  'px-2 py-0.5 md:px-3 md:py-1',
  'rounded-r-[200px]',
  'max-w-[90%] truncate',
])

export type CategoryCardTitleVariants = VariantProps<typeof categoryCardTitleVariants>

/**
 * Category card skeleton - matches the new square aspect.
 */
export const categoryCardSkeletonVariants = cva('aspect-square w-full')

export type CategoryCardSkeletonVariants = VariantProps<typeof categoryCardSkeletonVariants>
